import { Injectable, signal, computed, TemplateRef } from '@angular/core';
import {
  NavBarConfig,
  SheetConfig,
  SheetActionItem,
  ActionDialogConfig,
  MenuButtonConfig,
} from 'src/app/shared/types/NavigationBarsConfig.model';
import { Color } from 'src/app/shared/types/Color.model';

/**
 * Default bottom nav bar configuration.
 * Shared by both the initial state and the `resetAll()` method.
 */
const DEFAULT_BOTTOM_NAV: NavBarConfig = {
  visible: null,
  content: null,
  menuButton: undefined,
  color: 'blue',
  transparent: false,
};

/**
 * Default sheet configuration.
 */
const DEFAULT_SHEET: SheetConfig = {
  visible: false,
  title: undefined,
  description: undefined,
  content: null,
};

/**
 * Default action dialog configuration.
 */
const DEFAULT_ACTION_DIALOG: ActionDialogConfig = {
  visible: false,
  title: undefined,
  content: null,
  maxWidth: 700,
  showCloseButton: true,
  closeOnBackdropClick: true,
  closeOnEscape: true,
};

/**
 * Signal-based replacement for NGXS `NavigationBarsState` + `NavigationBarsService`.
 *
 * Manages the UI overlay state for the entire app: bottom nav bar, bottom sheet,
 * action dialog, and backdrop. All consumers call methods directly instead of
 * dispatching NGXS actions — no `Store`, no `@Select`, no `@Action`.
 *
 * @example
 * readonly nav = inject(NavigationState);
 *
 * // Open a sheet
 * this.nav.setSheet({ visible: true, title: 'Options', content: items });
 *
 * // Close it
 * this.nav.closeSheet();
 *
 * // Read state in a template
 * @if (nav.sheet().visible) { <app-bottom-sheet /> }
 *
 * @feature shared
 */
@Injectable({ providedIn: 'root' })
export class NavigationState {
  // ── Private writable signals ───────────────────────────────────────────

  private readonly _bottomNav = signal<NavBarConfig>({ ...DEFAULT_BOTTOM_NAV });
  private readonly _sheet = signal<SheetConfig>({ ...DEFAULT_SHEET });
  private readonly _actionDialog = signal<ActionDialogConfig>({ ...DEFAULT_ACTION_DIALOG });
  private readonly _backdrop = signal<boolean>(false);

  // ── Public readonly signals ────────────────────────────────────────────

  /** Bottom navigation bar configuration. */
  readonly bottomNav = this._bottomNav.asReadonly();

  /** Bottom sheet configuration. */
  readonly sheet = this._sheet.asReadonly();

  /** Action dialog configuration. */
  readonly actionDialog = this._actionDialog.asReadonly();

  /** Backdrop overlay visibility. */
  readonly backdrop = this._backdrop.asReadonly();

  // ── Convenience computed signals ───────────────────────────────────────

  /** Whether the bottom nav bar is currently visible. */
  readonly bottomNavVisible = computed(() => this._bottomNav().visible);

  /** Whether the bottom nav bar is transparent. */
  readonly bottomNavTransparent = computed(() => this._bottomNav().transparent ?? false);

  /** Bottom nav bar content (template ref or array). */
  readonly bottomNavContent = computed(() => this._bottomNav().content);

  /** Bottom nav bar menu button config. */
  readonly bottomNavMenuButton = computed(() => this._bottomNav().menuButton);

  /** Bottom nav bar color. */
  readonly bottomNavColor = computed(() => this._bottomNav().color);

  /** Whether the sheet is currently visible. */
  readonly sheetVisible = computed(() => this._sheet().visible);

  /** Whether the action dialog is currently visible. */
  readonly actionDialogVisible = computed(() => this._actionDialog().visible);

  // ── Bottom nav bar ─────────────────────────────────────────────────────

  /** Merge a partial config into the bottom nav bar state. */
  setBottomNavBar(config: Partial<NavBarConfig>): void {
    this._bottomNav.update(prev => ({ ...prev, ...config }));
  }

  /** Set the bottom nav bar visibility. */
  setBottomNavBarVisibility(visible: boolean): void {
    this._bottomNav.update(prev => ({ ...prev, visible }));
  }

  /** Show the bottom nav bar. */
  showBottomNavBar(): void {
    this.setBottomNavBarVisibility(true);
  }

  /** Hide the bottom nav bar. */
  hideBottomNavBar(): void {
    this.setBottomNavBarVisibility(false);
  }

  /** Toggle the bottom nav bar visibility. */
  toggleBottomNavBarVisibility(): void {
    this._bottomNav.update(prev => ({ ...prev, visible: !prev.visible }));
  }

  /** Set the bottom nav bar content. */
  setBottomNavBarContent(content: unknown): void {
    this._bottomNav.update(prev => ({ ...prev, content }));
  }

  /** Set the bottom nav bar menu button config. */
  setBottomNavBarMenuButton(config: Partial<MenuButtonConfig>): void {
    this._bottomNav.update(prev => ({
      ...prev,
      menuButton: { ...(prev.menuButton ?? { visible: false, icon: '' }), ...config },
    }));
  }

  /** Set the bottom nav bar transparent flag. */
  setBottomNavBarTransparent(transparent: boolean): void {
    this._bottomNav.update(prev => ({ ...prev, transparent }));
  }

  /** Toggle the bottom nav bar transparent flag. */
  toggleBottomNavBarTransparent(): void {
    this._bottomNav.update(prev => ({ ...prev, transparent: !prev.transparent }));
  }

  /** Update a specific property on a button in the bottom nav bar content array. */
  updateBottomNavBarButtonProperty(update: {
    index: number;
    property: string;
    value: unknown;
  }): void {
    this._bottomNav.update(prev => {
      if (!Array.isArray(prev.content)) return prev;
      const content = [...prev.content];
      if (update.index >= 0 && update.index < content.length) {
        content[update.index] = { ...content[update.index], [update.property]: update.value };
      }
      return { ...prev, content };
    });
  }

  // ── Bottom sheet ───────────────────────────────────────────────────────

  /** Open / configure the bottom sheet. Merges into the current config. */
  setSheet(config: Partial<SheetConfig>): void {
    this._sheet.update(prev => ({ ...prev, ...config }));
  }

  /** Set the bottom sheet visibility. */
  setSheetVisibility(visible: boolean): void {
    this._sheet.update(prev => ({ ...prev, visible }));
  }

  /** Toggle the bottom sheet visibility. */
  toggleSheetVisibility(): void {
    this._sheet.update(prev => ({ ...prev, visible: !prev.visible }));
  }

  /** Set the bottom sheet content. */
  setSheetContent(content: SheetConfig['content']): void {
    this._sheet.update(prev => ({ ...prev, content }));
  }

  /** Close the bottom sheet and reset its config to defaults. */
  closeSheet(): void {
    this._sheet.set({ ...DEFAULT_SHEET });
  }

  // ── Action dialog ──────────────────────────────────────────────────────

  /** Open / configure the action dialog. Merges into the current config. */
  setActionDialog(config: Partial<ActionDialogConfig>): void {
    this._actionDialog.update(prev => ({ ...prev, ...config }));
  }

  /** Set the action dialog visibility. */
  setActionDialogVisibility(visible: boolean): void {
    this._actionDialog.update(prev => ({ ...prev, visible }));
  }

  /** Toggle the action dialog visibility. */
  toggleActionDialogVisibility(): void {
    this._actionDialog.update(prev => ({ ...prev, visible: !prev.visible }));
  }

  /** Set the action dialog content. */
  setActionDialogContent(content: TemplateRef<unknown> | null): void {
    this._actionDialog.update(prev => ({ ...prev, content }));
  }

  /** Close the action dialog and reset its config to defaults. */
  closeActionDialog(): void {
    this._actionDialog.set({ ...DEFAULT_ACTION_DIALOG });
  }

  // ── Backdrop ───────────────────────────────────────────────────────────

  /** Set backdrop visibility. */
  setBackdropVisibility(visible: boolean): void {
    this._backdrop.set(visible);
  }

  /** Show the backdrop. */
  showBackdrop(): void {
    this._backdrop.set(true);
  }

  /** Hide the backdrop. */
  hideBackdrop(): void {
    this._backdrop.set(false);
  }

  /** Toggle the backdrop. */
  toggleBackdrop(): void {
    this._backdrop.update(v => !v);
  }

  // ── Bulk operations ────────────────────────────────────────────────────

  /**
   * Merge an arbitrary partial config across all sub-states at once.
   * Useful for page-level setup where you configure the bottom nav, sheet
   * readiness, and backdrop in a single call.
   */
  setNavigationConfig(config: {
    bottom?: Partial<NavBarConfig>;
    sheet?: Partial<SheetConfig>;
    actionDialog?: Partial<ActionDialogConfig>;
    backdrop?: boolean;
  }): void {
    if (config.bottom) this.setBottomNavBar(config.bottom);
    if (config.sheet) this.setSheet(config.sheet);
    if (config.actionDialog) this.setActionDialog(config.actionDialog);
    if (config.backdrop !== undefined) this.setBackdropVisibility(config.backdrop);
  }

  /** Reset everything to defaults. */
  resetAll(): void {
    this._bottomNav.set({ ...DEFAULT_BOTTOM_NAV });
    this._sheet.set({ ...DEFAULT_SHEET });
    this._actionDialog.set({ ...DEFAULT_ACTION_DIALOG });
    this._backdrop.set(false);
  }
}
